import { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from 'react-fetching-library';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import showServerError from 'helpers/showError';
import { getTransitionWithVariable } from 'redux_store';
import ClaimUploadBox from './ClaimUploadBox';
import ClaimUploadResult from './ClaimUploadResult';
import ClaimUploadHistory from './ClaimUploadHistory';
import { uploadClaimsAction } from './api';
import { useAccountName } from 'hooks/useAccountName';
import { FAKE_TITLE_DATA } from 'components/Shared/FakeDataGenerator';

const useStyles = makeStyles(theme => ({
  content: {
    marginTop: theme.spacing(2),
  },
}));

const ClaimsUpload = () => {
  const classes = useStyles();
  const { accountUuid: paramAccountUuid } = useParams();
  const { name: accountName, loading: accountLoading } = useAccountName();
  const dispatch = useDispatch();
  const {
    en: { titles, claimUpload },
  } = useSelector(state => state.i18n);
  const currentAccountUuid = useSelector(state => state.user?.metadata?.currentAccount?.uuid);
  const accountUuid = paramAccountUuid || currentAccountUuid;

  const [result, setResult] = useState(null); // { response, validateOnly }
  // Which action is in flight, so the spinner lands on the clicked button:
  // 'validate' | 'import' | null. Both buttons disable while either runs.
  const [loadingMode, setLoadingMode] = useState(null);
  const historyRef = useRef(null);

  const { mutate } = useMutation(uploadClaimsAction);

  const run = async (file, { validateOnly }) => {
    setLoadingMode(validateOnly ? 'validate' : 'import');
    try {
      const { payload, error } = await mutate({ accountUuid, file, validateOnly });
      if (error || !payload) {
        showServerError(dispatch, payload?.message);
        return;
      }
      // Rejected uploads come back with an empty fileName — fall back to the
      // name of the file the user actually picked so the card has a title.
      setResult({
        response: { ...payload, fileName: payload.fileName || file?.name },
        validateOnly,
      });
      if (!validateOnly && payload.uploadId) historyRef.current?.refresh();
    } catch (e) {
      showServerError(dispatch, e?.message);
    } finally {
      setLoadingMode(null);
    }
  };

  const onInvalid = () => showServerError(dispatch, claimUpload.onlyXlsx);

  return (
    <ContentWrapper
      titleText={
        accountLoading
          ? FAKE_TITLE_DATA
          : getTransitionWithVariable(titles.claimsUploadForAccount, { accountName })
      }
    >
      <div className={classes.content}>
        <ClaimUploadBox onSubmit={{ run, onInvalid }} loadingMode={loadingMode} />
        {result && (
          <ClaimUploadResult
            response={result.response}
            validateOnly={result.validateOnly}
            onClose={() => setResult(null)}
          />
        )}
        <ClaimUploadHistory
          ref={historyRef}
          accountUuid={accountUuid}
          freshUploadId={result && !result.validateOnly ? result.response?.uploadId : null}
        />
      </div>
    </ContentWrapper>
  );
};

export default ClaimsUpload;
